import { Component, Suspense, useEffect, type ErrorInfo, type ReactNode } from 'react';
import { useRoute, type ViewId } from '@/lib/router';
import { preloadViews, viewComponent } from '@/lib/slots';
import { Callout, Empty, Loading } from '@/ui/bits';
import { Icon } from '@/ui/Icon';

// The current view, chosen by the address bar. Each view's code loads on first visit (or earlier, while the
// browser is idle) and a failing view shows a message with a retry instead of taking the whole app down.

export const VIEW_LABELS: Record<ViewId, string> = {
  today: 'Home',
  inbox: 'Inbox',
  people: 'People',
  companies: 'Companies',
  deals: 'Deals',
  finder: 'AI Finder',
  activity: 'Activity this week',
  bin: 'Bin',
};

/** Views that fill the window height and scroll inside (lists, the board, the inbox panes). */
const FULL_HEIGHT = new Set<ViewId>(['inbox', 'people', 'companies', 'deals']);

export function ViewHost() {
  const { view } = useRoute();

  useEffect(() => {
    document.getElementById('main')?.scrollTo({ top: 0 });
    document.title = `${VIEW_LABELS[view]} · Clientbook`;
  }, [view]);

  useEffect(() => {
    const w = window as Window & { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number; cancelIdleCallback?: (id: number) => void };
    if (w.requestIdleCallback) {
      const id = w.requestIdleCallback(preloadViews, { timeout: 4000 });
      return () => w.cancelIdleCallback?.(id);
    }
    const t = setTimeout(preloadViews, 1500);
    return () => clearTimeout(t);
  }, []);

  const View = viewComponent(view);
  return (
    <section key={view} id={`v-${view}`} className={FULL_HEIGHT.has(view) ? 'view view-full active' : 'view active'} aria-label={VIEW_LABELS[view]}>
      <ViewBoundary label={VIEW_LABELS[view]}>
        <Suspense fallback={<Loading label={`Loading ${VIEW_LABELS[view]}`} />}>
          {View ? <View /> : <Empty icon="layers" title={`${VIEW_LABELS[view]} is not available`} />}
        </Suspense>
      </ViewBoundary>
    </section>
  );
}

interface BoundaryState {
  error: Error | null;
}

/** Catches a failing view or sheet so the rest of the app keeps working. */
export class ViewBoundary extends Component<{ label: string; children: ReactNode }, BoundaryState> {
  state: BoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): BoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error(`[${this.props.label}] failed to show`, error, info.componentStack);
  }

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    // A newer version of the app was released while this tab was open: its old view files are gone.
    const stale = /dynamically imported module|Importing a module script failed|Failed to fetch/i.test(error.message);
    return (
      <div className="view-error">
        <Callout
          tone="danger"
          role="alert"
          title={stale ? 'Clientbook has been updated' : `${this.props.label} ran into a problem`}
          actions={
            stale ? (
              <button type="button" className="btn btn-sm btn-tile" onClick={() => window.location.reload()}>
                <Icon name="refresh" />
                Reload Clientbook
              </button>
            ) : (
              <button type="button" className="btn btn-sm btn-tile" onClick={() => this.setState({ error: null })}>
                <Icon name="refresh" />
                Try again
              </button>
            )
          }
        >
          {stale
            ? 'Reload to get the latest version. Anything you saved is kept.'
            : 'The rest of Clientbook still works. Try again, and if it keeps happening, reload the page.'}
        </Callout>
      </div>
    );
  }
}
